import React, { useMemo } from 'react';
import { sanitizeHtml } from 'magical-trails-react';
import type { Page, PhoneSender } from 'magical-trails';

import Avatar from '../Avatar';
import { clockFace } from '../clock';

/**
 * The pieces a thread is made of. Incoming pieces take an optional `from`:
 * set in group threads, on the first piece of a run, to say who is talking.
 */

interface FromProps {
  from?: PhoneSender;
}

const Incoming: React.FC<
  FromProps & {
    /** Direct threads show no name; a screen reader still needs one. */
    srName?: string;
    className?: string;
    children: React.ReactNode;
  }
> = ({ from, srName, className = '', children }) => (
  <div className={`Phone_row Phone_row-in ${from ? 'Phone_row-named' : ''}`}>
    {from && <Avatar name={from.name} src={from.avatar} size="sm" />}
    <div className={`Phone_rowBody ${className}`}>
      {from ? (
        <span className="Phone_rowName">{from.name}</span>
      ) : srName ? (
        <span className="sr-only">{srName}: </span>
      ) : null}
      {children}
    </div>
  </div>
);

interface HtmlBubbleProps extends FromProps {
  html: string;
  srName?: string;
  /** Several questions are open: this one can be picked to answer. */
  selected?: boolean;
  onSelect?: () => void;
}

const HtmlBubbleInner: React.FC<HtmlBubbleProps> = ({
  from,
  srName,
  html,
  selected,
  onSelect,
}) => {
  // The SAME object between renders. React re-applies innerHTML whenever it
  // is handed a new `{ __html }`, even for an identical string, and the
  // thread is a live region: every re-applied bubble was being read out
  // again each time a new one arrived.
  const inner = useMemo(() => ({ __html: sanitizeHtml(html) }), [html]);
  return (
    <Incoming from={from} srName={srName}>
      <div
        className={`Phone_bubble Phone_bubble-in ${
          selected ? 'Phone_bubble-selected' : ''
        }`}
        dangerouslySetInnerHTML={inner}
      />
      {onSelect && (
        // A button BESIDE the question, not the question as a button: author
        // HTML can hold links and lists, which a button must not.
        <button
          type="button"
          className="Phone_aim"
          onClick={onSelect}
          aria-pressed={selected}
        >
          {selected ? 'Replying to this' : 'Reply to this'}
        </button>
      )}
    </Incoming>
  );
};

export const HtmlBubble = React.memo(HtmlBubbleInner);

export const ImageBubble: React.FC<
  FromProps & { src: string; alt: string }
> = ({ from, src, alt }) => (
  <Incoming from={from}>
    <div className="Phone_bubble Phone_bubble-in Phone_bubble-media">
      <img src={src} alt={alt} loading="lazy" />
    </div>
  </Incoming>
);

export const MediaBubble: React.FC<
  FromProps & { page: Extract<Page, { type: 'audioPage' | 'videoPage' }> }
> = ({ from, page }) => (
  <Incoming from={from}>
    <div className="Phone_bubble Phone_bubble-in Phone_bubble-media">
      {page.type === 'audioPage' ? (
        <>
          {page.image && <img src={page.image} alt={page.altText ?? ''} />}
          {/* Never autoplays here: a message that starts talking when the
              thread opens is a surprise nobody asked for. */}
          <audio controls preload="metadata" src={page.audioUrl} />
          {page.transcript?.trim() && (
            <details className="Phone_transcript">
              <summary>Read what it says</summary>
              <p>{page.transcript}</p>
            </details>
          )}
        </>
      ) : (
        <video
          controls
          playsInline
          preload="metadata"
          poster={page.posterImage}
          src={page.videoUrl}
        />
      )}
    </div>
  </Incoming>
);

export const PlayerBubble: React.FC<{ text?: string; photo?: boolean }> = ({
  text,
  photo,
}) => (
  <div className="Phone_row Phone_row-out">
    <div className="Phone_bubble Phone_bubble-out">
      <span className="sr-only">You: </span>
      {photo ? (
        <span className="Phone_photoSent">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M4 8.5A2 2 0 0 1 6 6.5h1.6l1.2-1.8h6.4l1.2 1.8H18a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8.5Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
            <circle
              cx="12"
              cy="12.75"
              r="3.25"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
            />
          </svg>
          Photo sent
        </span>
      ) : (
        text
      )}
    </div>
  </div>
);

export const TypingBubble: React.FC<FromProps & { name: string }> = ({
  from,
  name,
}) => (
  <Incoming from={from}>
    <div
      className="Phone_bubble Phone_bubble-in Phone_typing"
      aria-hidden="true"
      data-typing={name}
    >
      <span />
      <span />
      <span />
    </div>
  </Incoming>
);

export const TimeChip: React.FC<{ at: number }> = ({ at }) => (
  <div className="Phone_timeChip">
    <time dateTime={new Date(at).toISOString()}>{clockFace(new Date(at))}</time>
  </div>
);

const CARD_ICONS: Record<'dossier' | 'camera' | 'activity', React.ReactNode> = {
  dossier: (
    <path d="M6 3.5h8.5L19 8v12.5H6v-17ZM14 3.5V8.5h5M9 12.5h7M9 16h5" />
  ),
  camera: (
    <>
      <path d="M4 8.5A2 2 0 0 1 6 6.5h1.6l1.2-1.8h6.4l1.2 1.8H18a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8.5Z" />
      <circle cx="12" cy="12.75" r="3.25" />
    </>
  ),
  activity: (
    <path d="M9.5 4.5h5v3.25a1.75 1.75 0 1 0 3.5 0V7h1.5v5h-.75a1.75 1.75 0 1 0 0 3.5H19.5v4h-5v-.75a1.75 1.75 0 1 0-3.5 0v.75h-6.5v-5h.75a1.75 1.75 0 1 0 0-3.5H4.5V7h5V4.5Z" />
  ),
};

export const AttachmentCard: React.FC<
  FromProps & {
    kind: 'dossier' | 'camera' | 'activity';
    title: string;
    detail: string;
    done: boolean;
    actionLabel: string;
    onOpen: () => void;
  }
> = ({ from, kind, title, detail, done, actionLabel, onOpen }) => (
  <Incoming from={from}>
    <div
      className={`Phone_card Phone_card-${kind} ${done ? 'Phone_card-done' : ''}`}
    >
      <span className="Phone_cardIcon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {CARD_ICONS[kind]}
        </svg>
      </span>
      <span className="Phone_cardText">
        <strong>{title}</strong>
        <span>{done ? `${detail} · Done` : detail}</span>
      </span>
      <button type="button" className="Phone_cardAction" onClick={onOpen}>
        {actionLabel}
      </button>
    </div>
  </Incoming>
);

export const LocationCard: React.FC<{
  title: string;
  address?: string;
  image?: string;
  actionLabel: string;
  onOpen: () => void;
}> = ({ title, address, image, actionLabel, onOpen }) => (
  <Incoming>
    <div className="Phone_card Phone_card-location">
      {image ? (
        <img className="Phone_cardImage" src={image} alt="" loading="lazy" />
      ) : (
        <span className="Phone_cardIcon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 21s6.5-5.7 6.5-10.5a6.5 6.5 0 1 0-13 0C5.5 15.3 12 21 12 21Z" />
            <circle cx="12" cy="10.5" r="2.25" />
          </svg>
        </span>
      )}
      <span className="Phone_cardText">
        <strong>{title}</strong>
        {address && <span>{address}</span>}
      </span>
      <button type="button" className="Phone_cardAction" onClick={onOpen}>
        {actionLabel}
      </button>
    </div>
  </Incoming>
);
